import { useState } from "react";
import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { Header } from "@/react/components/Chat/MessagingV2/RailBar/Header";
import { useBotCommentsInfinite } from "@/react/hooks/agent_profile/useBotCommentsInfinite";
import { chatEventsEmitter } from "@/react/hooks/chatsV2/chatEventsEmitter";
import { RailBarPortal } from "@circle-react/components/Layout/StandardLayoutV2/RailBarPortal";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { newChatRoomPath } from "@circle-react/helpers/urlHelpers";
import { useCommunityInboxStore } from "@circle-react/hooks/chatsV2/store";
import { useElementHeight } from "@circle-react/hooks/utils/useElementHeight";
import type { Profile } from "@circle-react/types/CommunityBot/Profile";
import { Icon } from "@circle-react-shared/Icon";
import { Loader } from "@circle-react-shared/uikit/Loader";
import { TiptapEditor } from "@circle-react-shared/uikit/TipTap";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Rail } from "@circle-react-uikit/Rail";
import { SuggestedQuestionButton } from "../Shared/SuggestedQuestionButton";
import { AgentAvatar } from "./AgentAvatar";
import { CommentsList } from "./CommentsList";
import { PrivacyNotice } from "./PrivacyNotice";

interface BotProfileRailbarProps {
  botProfile: Profile;
  conversationStatus?: "getting_too_long" | "too_long" | "exceeded" | "normal";
}

export const BotProfileRailbar = ({
  botProfile,
  conversationStatus = "normal",
}: BotProfileRailbarProps) => {
  const { ref: headerRef, height: headerHeight } = useElementHeight();
  const history = useHistory();
  const { setCurrentBotProfile } = useCommunityInboxStore();
  const [activeTab, setActiveTab] = useState<"about" | "comments">("about");

  const profileId = botProfile?.id;
  const {
    fetchNextPage,
    hasNextPage,
    isLoading: isCommentsLoading,
    error: commentsError,
    comments,
  } = useBotCommentsInfinite({ profileId, enabled: activeTab === "comments" });
  const commentsCount = botProfile?.comments_count ?? 0;

  const handleNewChat = () => {
    setCurrentBotProfile(botProfile);
    history.push({
      pathname: newChatRoomPath(),
    });
  };

  const formatMessageBody = (text: string) => ({
    type: "doc",
    content: [
      {
        type: "paragraph",
        content: [
          {
            type: "text",
            text: text,
          },
        ],
      },
    ],
  });

  const handleSendSuggestion = (question: string) => {
    const payload = {
      body: {
        body: formatMessageBody(question),
        attachments: [],
        localAttachments: [],
        localSgidToObjectMap: {},
      },
    };
    chatEventsEmitter.sendSuggestedQuestion(payload);
  };

  return (
    <RailBarPortal>
      <div ref={headerRef}>
        <Rail.Header className="!min-h-14">
          <Header title={t("profile")} isDirect />
        </Rail.Header>
      </div>
      <Rail.Content noPadding heightGap={headerHeight}>
        <div className="flex h-full flex-col">
          {/* Bot Avatar and Name - Always visible */}
          <div className="px-6 py-5">
            <div className="mb-4">
              <AgentAvatar profile={botProfile} />
              <div className="flex flex-col items-start gap-2">
                <div className="bg-tertiary dark:bg-secondary-dark flex h-6 items-center justify-center gap-1.5 rounded-full px-2 py-[3px]">
                  <Icon type="16-ask-ai-gradient" size={16} />
                  <span className="text-dark text-[12px] font-medium">
                    {t("community_bot.profile_tag")}
                  </span>
                </div>
                <Typography.TitleSm weight="semibold" color="text-dark">
                  {botProfile.name}
                </Typography.TitleSm>
              </div>
            </div>
            {botProfile.enabled && botProfile.allow_direct_messages && (
              <div>
                <button
                  type="button"
                  onClick={handleNewChat}
                  aria-label={t("chat_space.header.new_conversation")}
                  className="border-primary hover:bg-tertiary text-dark dark:border-secondary dark:text-dark dark:hover:bg-secondary flex items-baseline whitespace-normal rounded-[1.25rem] border px-3.5 py-2 text-left text-sm font-medium transition-colors"
                >
                  <Icon
                    aria-label={t("chat_space.header.new_conversation")}
                    type="new-conversation"
                    className="text-dark mr-1"
                    useWithFillCurrentColor
                    size={20}
                  />
                  <Typography.LabelSm weight="medium" color="text-dark">
                    {t("chat_space.header.new_conversation")}
                  </Typography.LabelSm>
                </button>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="border-primary relative border-b">
            <div className="flex gap-4 px-6">
              <button
                type="button"
                className={classNames(
                  "focus-visible:outline-secondary flex h-10 items-center justify-center border-b-2 border-transparent pb-3 pt-2 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                  activeTab === "about"
                    ? "border-darkest text-dark font-semibold"
                    : "text-default hover:text-dark",
                )}
                onClick={() => setActiveTab("about")}
              >
                {t("about")}
              </button>
              <button
                type="button"
                className={classNames(
                  "focus-visible:outline-secondary flex h-10 items-center justify-center border-b-2 border-transparent pb-3 pt-2 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                  activeTab === "comments"
                    ? "border-darkest text-dark font-semibold"
                    : "text-default hover:text-dark",
                )}
                onClick={() => setActiveTab("comments")}
              >
                {t("comments.comments")} {commentsCount > 0 && commentsCount}
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div
            className="flex flex-1 flex-col overflow-y-auto"
            id="bot-profile-railbar-scroll-target"
          >
            {activeTab === "about" ? (
              <div className="flex h-full flex-col">
                <div className="flex-1 px-6 py-5">
                  {botProfile.bio && (
                    <div className="mb-5 flex flex-col gap-y-2">
                      <Typography.LabelSm weight="semibold" color="text-dark">
                        {t("community_inbox.profile.bio")}
                      </Typography.LabelSm>
                      <div className="text-default text-sm font-normal leading-5 tracking-tighter">
                        <TiptapEditor
                          editable={false}
                          rich_text_body={botProfile.bio}
                          shouldShowPreviewBlocks={false}
                        />
                      </div>
                    </div>
                  )}
                  {botProfile.suggested_questions &&
                    botProfile.suggested_questions.length > 0 && (
                      <div className="mb-5 flex flex-col gap-y-2">
                        <Typography.LabelSm weight="semibold" color="text-dark">
                          {t(
                            "community_bot.profiles.suggested_questions_label",
                          )}
                        </Typography.LabelSm>
                        <div className="flex flex-col items-start gap-2">
                          {botProfile.suggested_questions.map(
                            (question: string) => (
                              <SuggestedQuestionButton
                                key={question}
                                question={question}
                                onClick={() => handleSendSuggestion(question)}
                                disabled={
                                  !botProfile.enabled ||
                                  !botProfile.allow_direct_messages ||
                                  conversationStatus === "too_long" ||
                                  conversationStatus === "exceeded"
                                }
                              />
                            ),
                          )}
                        </div>
                      </div>
                    )}
                </div>
                <div className="shrink-0 px-6 pb-5 text-center">
                  <PrivacyNotice />
                </div>
              </div>
            ) : (
              <div className="flex flex-col">
                {isCommentsLoading ? (
                  <div className="flex h-32 items-center justify-center">
                    <Loader />
                  </div>
                ) : (
                  <CommentsList
                    comments={comments}
                    fetchNextPage={fetchNextPage}
                    hasNextPage={!!hasNextPage}
                    isLoading={false}
                    error={commentsError}
                    scrollTargetId="bot-profile-railbar-scroll-target"
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </Rail.Content>
    </RailBarPortal>
  );
};
